import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {MilestoneEdit} from '@github-ui/repository-milestone/MilestoneEdit'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useEffect} from 'react'
import {type EntryPointComponent, graphql, type PreloadedQuery, usePreloadedQuery} from 'react-relay'

import {renderIssuesSidebar} from '../../components/sidebar/render-issues-sidebar'
import {useSetCurrentViewId} from '../../contexts/QueryContext'
import {useEntryPointsLoader} from '../../hooks/use-entrypoint-loaders'
import {RepositoryMilestonePage} from '../../lazy-routes'
import {preloadWhenIdle} from '../../utils/preload-when-idle'
import {AnalyticsWrapper} from '../AnalyticsWrapper'
import type {RepositoryMilestoneEditPageQuery} from './__generated__/RepositoryMilestoneEditPageQuery.graphql'
import type {RepositoryResult} from './repository-milestone-edit-page-query-types'

// RELAY-CLEANUP(RepositoryMilestoneEditPageQuery): widens nullable fields so the still-Relay
// parent query data is assignable. Remove when RepositoryMilestoneEditPageQuery leaves Relay.
export type RepositoryMilestoneEditPageContentInternalFragment = RelayCompat<
  Pick<RepositoryResult, 'id' | 'nameWithOwner' | 'viewerCanPush' | 'milestone'>
>

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query RepositoryMilestoneEditPageQuery($name: String!, $owner: String!, $number: Int!) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      nameWithOwner
      viewerCanPush
      milestone(number: $number) {
        id
        number
        title
        description
        dueOn
        closed
      }
    }
  }
`

export const RepositoryMilestoneEditPage: EntryPointComponent<
  {pageQuery: RepositoryMilestoneEditPageQuery},
  Record<string, never>
> = ({queries: {pageQuery}}) => {
  const {queryRef} = useEntryPointsLoader(pageQuery, PageQuery)
  if (!queryRef) return null

  return (
    <AnalyticsWrapper category="Repository Milestone Edit">
      <RepositoryMilestoneEditPageContent pageQueryRef={queryRef} />
    </AnalyticsWrapper>
  )
}

function RepositoryMilestoneEditPageContent({
  pageQueryRef,
}: {
  pageQueryRef: PreloadedQuery<RepositoryMilestoneEditPageQuery>
}) {
  const pageData = usePreloadedQuery<RepositoryMilestoneEditPageQuery>(PageQuery, pageQueryRef)

  useEffect(() => {
    return preloadWhenIdle(() => {
      RepositoryMilestonePage.preload()
    })
  }, [])

  const setCurrentViewId = useSetCurrentViewId()
  useEffect(() => {
    setCurrentViewId(VIEW_IDS.repository)
  }, [pageQueryRef, setCurrentViewId])

  const repositoryData = pageData?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  if (!okResult || !okResult.value) {
    return null
  }

  return <RepositoryMilestoneEditPageContentInternal repository={okResult.value} />
}

function RepositoryMilestoneEditPageContentInternal({
  repository,
}: {
  repository: RepositoryMilestoneEditPageContentInternalFragment
}) {
  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  return (
    <RepoIssuesPageLayout
      dialogTitle="Milestones"
      title={sidebarLayoutEnabled ? 'Edit milestone' : undefined}
      renderSidebar={renderIssuesSidebar}
    >
      <MilestoneEdit repository={repository} />
    </RepoIssuesPageLayout>
  )
}
